import React, { useEffect, useState } from 'react';
import { Card, Table, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic, faSearch, faGlobe, faEyeSlash, faHeart } from '@fortawesome/free-solid-svg-icons';
import { getPublicSongs, updateSongVisibility } from '../../services/exploreApi';
import Pagination from '../common/Pagination';

const SORT_OPTIONS = [
  { value: 'public_at_desc',   label: 'Public date (newest)' },
  { value: 'public_at_asc',    label: 'Public date (oldest)' },
  { value: 'created_at_desc',  label: 'Created (newest)' },
  { value: 'favour_count_desc', label: 'Most favoured' },
  { value: 'last_favourited_at_desc', label: 'Recently favoured' },
];

const ExplorePublicSongs = () => {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [isPublicFilter, setIsPublicFilter] = useState('all');
  const [sort, setSort] = useState('public_at_desc');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [taskId, setTaskId] = useState('');
  const [formIsPublic, setFormIsPublic] = useState(true);
  const [formLoading, setFormLoading] = useState(false);
  const [formMessage, setFormMessage] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const fetchSongs = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, items_per_page: pageSize, sort };
      if (isPublicFilter !== 'all') params.is_public = isPublicFilter;
      if (appliedSearch.trim()) params.search = appliedSearch.trim();
      const res = await getPublicSongs(params);
      setSongs(res.data?.data || []);
      const objs = res.data?.objects;
      setTotal(objs?.total ?? objs?.count ?? 0);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch');
      setSongs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSongs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isPublicFilter, sort, appliedSearch]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!taskId.trim()) return;
    setFormLoading(true);
    setFormMessage(null);
    try {
      await updateSongVisibility({ task_id: taskId.trim(), is_public: formIsPublic });
      setFormMessage({ type: 'success', text: 'Visibility updated' });
      setTaskId('');
      fetchSongs();
    } catch (err) {
      setFormMessage({ type: 'danger', text: err.response?.data?.message || err.message || 'Failed' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggle = async (song) => {
    setTogglingId(song.id);
    try {
      await updateSongVisibility({ task_id: song.task_id, is_public: !song.is_public });
      fetchSongs();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div>
      <h2 className="mb-4">
        <FontAwesomeIcon icon={faMusic} className="me-2" />
        Public Songs
      </h2>

      <Card className="mb-4">
        <Card.Header>Update visibility</Card.Header>
        <Card.Body>
          <Form onSubmit={handleFormSubmit} className="row g-3 align-items-end">
            <div className="col-md-4">
              <Form.Label>Task ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="abc-xyz"
                value={taskId}
                onChange={(e) => setTaskId(e.target.value)}
                disabled={formLoading}
              />
            </div>
            <div className="col-md-2">
              <Form.Check
                type="checkbox"
                id="formIsPublic"
                label="Public"
                checked={formIsPublic}
                onChange={(e) => setFormIsPublic(e.target.checked)}
              />
            </div>
            <div className="col-md-2">
              <Button type="submit" variant="primary" disabled={formLoading || !taskId.trim()}>
                {formLoading ? <Spinner animation="border" size="sm" /> : 'Update'}
              </Button>
            </div>
          </Form>
          {formMessage && (
            <Alert variant={formMessage.type} className="mt-2 mb-0 py-2">{formMessage.text}</Alert>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <span>Song list</span>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <Form.Select
              className="form-control form-select"
              style={{ width: 'auto' }}
              value={isPublicFilter}
              onChange={(e) => { setIsPublicFilter(e.target.value); setPage(1); }}
            >
              <option value="all">All</option>
              <option value="true">Public</option>
              <option value="false">Private</option>
            </Form.Select>
            <Form.Select
              className="form-control form-select"
              style={{ width: 'auto' }}
              value={sort}
              onChange={(e) => { setSort(e.target.value); setPage(1); }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Form.Select>
            <div className="input-group" style={{ width: 220 }}>
              <input
                className="form-control"
                placeholder="Search task_id, title"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setAppliedSearch(search);
                    setPage(1);
                  }
                }}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => { setAppliedSearch(search); setPage(1); }}
              >
                <FontAwesomeIcon icon={faSearch} />
              </button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : songs.length === 0 ? (
            <p className="text-muted text-center py-4">No songs found</p>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Task ID</th>
                    <th>Title</th>
                    <th>Creator</th>
                    <th>Status</th>
                    <th><FontAwesomeIcon icon={faHeart} className="me-1 text-danger" />Favours</th>
                    <th>Public at</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {songs.map((s) => (
                    <tr key={s.id}>
                      <td><code>{s.task_id}</code></td>
                      <td>{s.title || '—'}</td>
                      <td>{s.creator_display_name || s.user_id || '—'}</td>
                      <td>
                        <Badge bg={s.is_public ? 'success' : 'secondary'}>
                          {s.is_public ? 'Public' : 'Private'}
                        </Badge>
                      </td>
                      <td>{s.favour_count ?? 0}</td>
                      <td>{s.public_at ? new Date(s.public_at * 1000).toLocaleString() : '—'}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleToggle(s)}
                          disabled={togglingId === s.id}
                        >
                          {togglingId === s.id
                            ? <Spinner animation="border" size="sm" />
                            : s.is_public
                              ? <FontAwesomeIcon icon={faEyeSlash} />
                              : <FontAwesomeIcon icon={faGlobe} />
                          }
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Pagination
                currentPage={page}
                totalItems={total}
                itemsPerPage={pageSize}
                onPageChange={setPage}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default ExplorePublicSongs;
